import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import { NoteType, NotePriority } from '../../../../../types/api';
import { NoteFormData } from '../hooks';

interface NoteFormFieldsProps {
  formData: NoteFormData;
  errors: Partial<Record<keyof NoteFormData, string>>;
  onFieldChange: <K extends keyof NoteFormData>(field: K, value: NoteFormData[K]) => void;
  disabled?: boolean;
}

const NOTE_TYPES: { value: NoteType; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'customer_feedback', label: 'Customer Feedback' },
  { value: 'alert', label: 'Alert' },
  { value: 'incident_accident', label: 'Incident/Accident' },
];

const PRIORITIES: { value: NotePriority; label: string; color?: string }[] = [
  { value: 'information', label: 'Information', color: '#9e9e9e' },
  { value: 'critical', label: 'Critical', color: '#f44336' },
];

export const NoteFormFields: React.FC<NoteFormFieldsProps> = ({
  formData,
  errors,
  onFieldChange,
  disabled = false,
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Subject Field */}
      <TextField
        label="Subject"
        value={formData.subject}
        onChange={(e) => onFieldChange('subject', e.target.value)}
        error={!!errors.subject}
        helperText={errors.subject}
        disabled={disabled}
        fullWidth
        required
      />

      {/* Type and Priority Row */}
      <Box display="flex" gap={2}>
        <FormControl error={!!errors.note_type} disabled={disabled} sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.note_type}
            label="Type"
            onChange={(e) => onFieldChange('note_type', e.target.value as NoteType)}
          >
            {NOTE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          {errors.note_type && <FormHelperText>{errors.note_type}</FormHelperText>}
        </FormControl>

        <FormControl error={!!errors.priority} disabled={disabled} sx={{ minWidth: 150 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={formData.priority}
            label="Priority"
            onChange={(e) => onFieldChange('priority', e.target.value as NotePriority)}
          >
            {PRIORITIES.map((priority) => (
              <MenuItem key={priority.value} value={priority.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: priority.color,
                    }}
                  />
                  {priority.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.priority && <FormHelperText>{errors.priority}</FormHelperText>}
        </FormControl>
      </Box>

      {/* Content Field */}
      <TextField
        label="Content"
        value={formData.content}
        onChange={(e) => onFieldChange('content', e.target.value)}
        error={!!errors.content}
        helperText={errors.content}
        disabled={disabled}
        multiline
        rows={4}
        fullWidth
        required
      />
    </Box>
  );
};
